import { useCallback, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { useGSAPContext } from "@/lib/gsap-context";

const items = [
  {
    quote: "Lead response time dropped from hours to seconds. Our WhatsApp basically books site visits while we sleep.",
    name: "Rohit Malhotra",
    role: "Director, Malhotra Realtors",
  },
  {
    quote: "Our front-desk doesn't drown in calls anymore — and we've cut no-shows almost in half.",
    name: "Dr. Priya Sharma",
    role: "Owner, SmileCare Dental Clinics",
  },
  {
    quote: "DIBRAL didn't just give us tools, they re-engineered our operations. The team is genuinely founder-grade.",
    name: "Arjun Mehta",
    role: "Founder, Indicara D2C",
  },
  {
    quote: "We finally see the real numbers across stores in one dashboard. Decisions are 10× faster.",
    name: "Vikram Rao",
    role: "COO, Rao's Kitchen",
  },
  {
    quote: "The AI replies feel human. Customers can't tell, and our CSAT jumped to 4.8.",
    name: "Neha Iyer",
    role: "Head of Support, LearnNext EdTech",
  },
  {
    quote: "My patients book home visits on WhatsApp without calling. The AI handles queries, reminders and follow-ups — it feels like having a full-time assistant.",
    name: "Dr. Gaurav Prajapati",
    role: "Founder, Neuro Fastheal",
  },
];

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function company(name) {
  return name.replace(/^.*,\s*/, "");
}

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const count = items.length;
  const quoteRef = useRef(null);
  const tweenRef = useRef(null);
  const { reducedMotion } = useGSAPContext();

  const go = useCallback((next) => {
    const nextIndex = (next + count) % count;
    setDir(nextIndex > index ? 1 : -1);
    setIndex(nextIndex);
  }, [count, index]);

  useEffect(() => {
    const id = setInterval(() => {
      setDir(1);
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearInterval(id);
  }, [count, index]);

  useEffect(() => {
    if (reducedMotion || !quoteRef.current) return;
    if (tweenRef.current) tweenRef.current.kill();

    gsap.set(quoteRef.current, { opacity: 0, x: dir * 40 });
    tweenRef.current = gsap.to(quoteRef.current, {
      opacity: 1,
      x: 0,
      duration: 0.55,
      ease: "power3.out",
    });

    return () => {
      if (tweenRef.current) tweenRef.current.kill();
    };
  }, [index, dir, reducedMotion]);

  const t = items[index];

  return (
    <section className="section relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0 aurora opacity-40" />
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-4">
            <Reveal direction="up">
              <span className="eyebrow">
                <span className="dot" /> Testimonials
              </span>
            </Reveal>
            <Reveal direction="up" delay={0.05}>
              <h2 className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]">
                Words from the businesses we{" "}
                <span className="font-serif italic gradient-text">work with.</span>
              </h2>
            </Reveal>
            <Reveal direction="up" delay={0.1}>
              <p className="mt-5 max-w-sm text-sm text-[var(--body-fg)]/55">
                * Results shared by clients and partners we have worked with.
              </p>
            </Reveal>

            <Reveal direction="up" delay={0.15}>
              <div className="mt-8 hidden lg:flex flex-col gap-2">
                {items.map((item, i) => {
                  const active = i === index;
                  return (
                    <button
                      key={item.name}
                      onClick={() => go(i)}
                      className={`flex items-center gap-3 text-left rounded-2xl px-3 py-2.5 border transition-all duration-300 ${
                        active
                          ? "bg-[var(--eyebrow-bg)] border-[var(--card-hover-border)] shadow-lg shadow-[var(--brand-glow)]/20"
                          : "bg-transparent border-transparent hover:bg-[var(--eyebrow-bg)] hover:border-[var(--glass-border)]"
                      }`}
                      aria-label={`Show testimonial from ${item.name}`}
                    >
                      <span className="avatar-orb h-9 w-9 text-xs shrink-0">{initials(item.name)}</span>
                      <div className="min-w-0">
                        <div className={`text-sm font-medium truncate ${active ? "text-[var(--body-fg)]" : "text-[var(--body-fg)]/70"}`}>
                          {item.name}
                        </div>
                        <div className="text-[11px] text-[var(--body-fg)]/45 truncate">{company(item.role)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal direction="up" delay={0.1}>
              <div
                className="glass glow-border rounded-3xl p-8 sm:p-10 relative overflow-hidden"
                style={{ boxShadow: "0 30px 70px -30px var(--brand-glow)" }}
              >
                <Quote className="absolute top-6 right-6 w-10 h-10 text-[var(--brand)]/15" />
                <div className="flex items-center gap-1 text-[var(--brand)]" data-testid={`testimonial-${index}`}>
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-current" />
                  ))}
                </div>

                <div className="mt-6 min-h-[130px]">
                  <blockquote
                    ref={quoteRef}
                    className="text-[var(--body-fg)]/85 leading-relaxed text-lg sm:text-xl"
                  >
                    "{t.quote}"
                  </blockquote>
                </div>

                <div className="mt-8 pt-6 border-t border-[var(--hairline)] flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="avatar-orb h-11 w-11 text-sm shrink-0">{initials(t.name)}</span>
                    <div className="min-w-0">
                      <div className="text-[var(--body-fg)] font-medium text-sm truncate">{t.name}</div>
                      <div className="text-[var(--brand)]/80 text-xs mt-0.5 font-mono truncate">{company(t.role)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => go(index - 1)}
                      aria-label="Previous testimonial"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--glass-border)] text-[var(--body-fg)]/70 hover:text-[var(--brand)] hover:border-[var(--card-hover-border)] transition"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => go(index + 1)}
                      aria-label="Next testimonial"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--glass-border)] text-[var(--body-fg)]/70 hover:text-[var(--brand)] hover:border-[var(--card-hover-border)] transition"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="mt-6 flex items-center justify-center gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === index ? 24 : 8,
                    background: i === index ? "var(--brand)" : "var(--glass-border)",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
